import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type {
  ErrorLike,
  OperationVariables,
  TypedDocumentNode,
} from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useAuth } from "../auth";

export interface BaseOrganization {
  id: string;
  isDefault?: boolean | null;
}

export interface CurrentTenantInfo {
  id?: string;
  parentId?: string | null;
  type?: string | null;
  resellerId?: number | null;
}

export interface UseOrganizationSelectionOptions<
  TOrg extends BaseOrganization,
  TOrgQuery,
  TOrgVars extends OperationVariables,
  TTenantQuery,
> {
  organizationsDocument: TypedDocumentNode<TOrgQuery, TOrgVars>;
  currentTenantDocument: TypedDocumentNode<TTenantQuery, Record<string, never>>;
  /** Builds the organizations query variables for the given (own) tenant id. */
  getOrganizationsVariables: (tenantId: string) => TOrgVars;
  /** Extracts a flat organization list from the raw query result. Define at module scope for a stable reference. */
  mapOrganizations: (data: TOrgQuery | undefined) => TOrg[];
  /** Extracts the current tenant's id/parentId/type from the raw query result. Define at module scope for a stable reference. */
  mapCurrentTenant: (
    data: TTenantQuery | undefined,
  ) => CurrentTenantInfo | undefined;
  /** Whether the organizations query should run at all, e.g. brand/mode gating. */
  enabled: boolean;
  /** Called when the current tenant turns out to have a parent (backoffice referral cleanup). */
  onParentTenantDetected?: () => void;
  /** localStorage key used to persist the selected organization id. Defaults to 'selectedOrganizationId'. */
  storageKey?: string;
}

export interface OrganizationSelectionResult<TOrg, TOrgQuery> {
  currentOrganization: TOrg | null;
  organizations: TOrg[];
  setCurrentOrganization: (org: TOrg) => void;
  loading: boolean;
  /** Tenant id from the current-tenant query (API); not from the user JWT. */
  currentTenantId: string | undefined;
  organizationsQueryData: TOrgQuery | undefined;
  organizationsQueryError: ErrorLike | undefined;
  tenantQueryError: ErrorLike | undefined;
  refetchOrganizations: () => Promise<unknown>;
  isResellerTenant: boolean;
  /** `true` once the tenant query resolved to a reseller tenant with a `resellerId` assigned. */
  hasResellerAccess: boolean;
}

/**
 * Fetches the current tenant + its own organizations, and manages selection of
 * the "current" organization (persisted in localStorage). Shared between
 * backoffice and reseller; each app supplies its own generated documents and
 * mapping functions since the generated GraphQL types are per-app.
 */
export function useOrganizationSelection<
  TOrg extends BaseOrganization,
  TOrgQuery,
  TOrgVars extends OperationVariables,
  TTenantQuery,
>(
  options: UseOrganizationSelectionOptions<
    TOrg,
    TOrgQuery,
    TOrgVars,
    TTenantQuery
  >,
): OrganizationSelectionResult<TOrg, TOrgQuery> {
  const {
    organizationsDocument,
    currentTenantDocument,
    getOrganizationsVariables,
    mapOrganizations,
    mapCurrentTenant,
    enabled,
    onParentTenantDetected,
    storageKey = "selectedOrganizationId",
  } = options;

  const [currentOrganization, setCurrentOrganization] = useState<TOrg | null>(
    null,
  );
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const prevUserIdRef = useRef<string | undefined>(undefined);

  const skipTenantQuery = authLoading || !isAuthenticated;

  const {
    data: tenantData,
    loading: tenantLoading,
    error: tenantError,
    refetch: refetchTenant,
  } = useQuery(currentTenantDocument, {
    skip: skipTenantQuery,
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const tenantInfo = mapCurrentTenant(tenantData);
  const currentTenantId = tenantInfo?.id;

  useEffect(() => {
    if (tenantInfo?.parentId) {
      onParentTenantDetected?.();
    }
  }, [tenantInfo?.parentId, onParentTenantDetected]);

  useEffect(() => {
    if (skipTenantQuery) {
      return;
    }
    if (
      prevUserIdRef.current !== undefined &&
      prevUserIdRef.current !== user?.id
    ) {
      void refetchTenant();
    }
    prevUserIdRef.current = user?.id;
  }, [user?.id, skipTenantQuery, refetchTenant]);

  const skipOrganizationsQuery =
    authLoading ||
    !isAuthenticated ||
    tenantLoading ||
    !currentTenantId ||
    !enabled;

  const {
    data: rawData,
    loading: queryLoading,
    error,
    refetch,
  } = useQuery(
    organizationsDocument,
    // Apollo's generic overloads can't be resolved against an unbound TOrgVars type
    // parameter here; the shape is guaranteed correct by the generic constraint above.
    {
      skip: skipOrganizationsQuery,
      variables: getOrganizationsVariables(currentTenantId ?? ""),
      fetchPolicy: "network-only",
      errorPolicy: "all",
    } as never,
  );
  // errorPolicy: 'all' widens Apollo's inferred type to allow partial data; the
  // shape at runtime matches TOrgQuery exactly as it did before extraction.
  const data = rawData as TOrgQuery | undefined;

  const loading = authLoading || tenantLoading || queryLoading;

  const organizations = useMemo(
    () => mapOrganizations(data),
    [data, mapOrganizations],
  );

  const refetchOrganizations = useCallback(() => {
    if (!currentTenantId) {
      return Promise.resolve(undefined);
    }
    return refetch(getOrganizationsVariables(currentTenantId));
  }, [currentTenantId, refetch, getOrganizationsVariables]);

  useEffect(() => {
    if (loading || organizations.length === 0) return;

    const savedOrgId = localStorage.getItem(storageKey);
    const savedOrg = savedOrgId
      ? organizations.find((org) => String(org.id) === savedOrgId)
      : null;

    if (savedOrg) {
      setCurrentOrganization(savedOrg);
    } else {
      const defaultOrg =
        organizations.find((org) => org.isDefault) ?? organizations[0];
      if (defaultOrg) {
        localStorage.setItem(storageKey, String(defaultOrg.id));
        setCurrentOrganization(defaultOrg);
      }
    }
  }, [loading, organizations, storageKey]);

  useEffect(() => {
    setCurrentOrganization(null);
  }, [isAuthenticated, user?.id, currentTenantId]);

  const handleSetCurrentOrganization = useCallback(
    (org: TOrg) => {
      setCurrentOrganization(org);
      localStorage.setItem(storageKey, String(org.id));
    },
    [storageKey],
  );

  return {
    currentOrganization,
    organizations,
    setCurrentOrganization: handleSetCurrentOrganization,
    loading,
    currentTenantId,
    organizationsQueryData: data,
    organizationsQueryError: error,
    tenantQueryError: tenantError,
    refetchOrganizations,
    isResellerTenant: tenantInfo?.type === "RESELLER",
    hasResellerAccess:
      tenantInfo?.type === "RESELLER" && tenantInfo?.resellerId != null,
  };
}
